// wikidataHelpers.js
// Helper functions for Wikidata entity search and linking

import {TextSelection} from 'prosemirror-state';
import {generateUUID} from './utils';
import {resolveEntityAvatarUrl} from '../../../utils/entityAvatarUrl';
import {getEntityKBUrl, getWikidataUrl, isLocalEntityRef} from '../../../utils/entityLinks';
import {coalesceEntityType} from '../../../utils/entityTypeMeta';
import {getUser} from '../../../auth/jwtAuth';
import {findWikidataMarkRange} from '../plugins/inlineInteractionPlugin';
import {
    fetchWikidataEntity as fetchSharedWikidataEntity,
    searchWikidataEntities as searchSharedWikidataEntities,
} from '../../../utils/wikidata';

export const MANUAL_WIKIDATA_LINK_SOURCE = 'manual-annotation';

const QID_RE = /^Q\d+$/i;

function isLinkedWikidataId(id) {
    return typeof id === 'string' && (QID_RE.test(id.trim()) || isLocalEntityRef(id));
}

/**
 * Search for Wikidata entities by query string
 * @param {string} query - Search query
 * @returns {Promise<Array>} Array of entity results
 */
export async function searchWikidataEntities(query) {
    return searchSharedWikidataEntities(query);
}

/**
 * Fetch detailed information about a Wikidata entity
 * @param {string} id - Wikidata entity ID (e.g., "Q42")
 * @returns {Promise<Object|null>} Entity data with label, description, and image
 */
export async function fetchWikidataEntity(id) {
    return fetchSharedWikidataEntity(id);
}

/**
 * Generate a unique mark instance identifier for wikidataEntity marks.
 * The Wikidata id (Qxxx) can repeat in a document; this uid identifies one span.
 * @returns {string}
 */
export function generateWikidataEntityUid() {
    return `wde-${generateUUID()}`;
}

/**
 * Build normalized attrs for a wikidataEntity mark.
 * @param {Object} entity
 * @param {string} [entity.id]
 * @param {string} [entity.label]
 * @param {string} [entity.url]
 * @param {string} [entity.description]
 * @param {string} [entity.imageUrl]
 * @param {string} [entity.image]
 * @param {Object} [options]
 * @param {string} [options.uid] - Reuse an existing mark uid when upgrading a mention
 * @param {string} [options.source] - Provenance marker (e.g. manual-annotation)
 * @param {string} [options.linkedBy] - Display name of the user who linked manually
 * @param {number|null} [options.linkingScore] - Override linking confidence
 * @returns {{id: string, uid: string, label: string|null, url: string|null, description: string|null, imageUrl: string|null}}
 */
export function buildWikidataEntityMarkAttrs(entity, options = {}) {
    const id = entity?.id;
    const rawImageUrl = entity?.imageUrl || entity?.image || null;
    const imageUrl = resolveEntityAvatarUrl({
        wikidataId: id,
        entityId: entity?.entityId,
        imageUrl: rawImageUrl,
    });
    const explicitConfidence = entity?.confidence != null && entity.confidence !== ''
        ? entity.confidence
        : null;
    const nerScore = options.nerScore != null && options.nerScore !== ''
        ? options.nerScore
        : (entity?.nerScore != null && entity.nerScore !== ''
            ? entity.nerScore
            : explicitConfidence);
    const linkingScore = options.linkingScore != null && options.linkingScore !== ''
        ? options.linkingScore
        : (entity?.linkingScore != null && entity.linkingScore !== ''
            ? entity.linkingScore
            : null);
    // Legacy single score mirrors the mention (ner) score so old readers still see a value.
    const confidence = explicitConfidence != null ? explicitConfidence : nerScore;
    const source = options.source || entity?.source || null;
    const linkedBy = options.linkedBy || entity?.linkedBy || null;
    const local = isLocalEntityRef(id) || entity?.local === true;
    return {
        id,
        uid: options.uid || generateWikidataEntityUid(),
        label: entity?.label || entity?.canonicalName || null,
        url: entity?.url || (local ? (id ? getEntityKBUrl(id) : null) : (getWikidataUrl(id) || null)),
        description: entity?.description || null,
        imageUrl,
        // Carry entity type + scores so the hover card shows them consistently with the
        // data-items / corpus renderers. All are optional — older entity payloads omit them.
        entityType: coalesceEntityType(options.entityType, entity?.entityType),
        confidence,
        nerScore,
        linkingScore,
        source,
        linkedBy,
    };
}

function collectWikidataMarksInRange(state, from, to, markType) {
    const marks = [];
    state.doc.nodesBetween(from, to, (node) => {
        if (!node.isText) return;
        for (const mark of node.marks) {
            if (mark.type === markType) {
                marks.push(mark);
            }
        }
    });
    return marks;
}

/**
 * Replace every wikidataEntity mark in a range with a single linked mark.
 * Clears stacked mention+link marks so manual links behave like machine links.
 *
 * @param {import('prosemirror-view').EditorView} view
 * @param {Object} entity - Selected Wikidata entity
 * @param {{from?: number, to?: number, uid?: string|null}} [rangeHint]
 * @returns {{applied: boolean, from: number, to: number, uid: string|null}}
 */
export function applyWikidataEntityLink(view, entity, rangeHint = {}) {
    const {schema, doc} = view.state;
    const markType = schema.marks.wikidataEntity;
    if (!markType || !entity?.id) {
        return {applied: false, from: 0, to: 0, uid: null};
    }

    const docSize = doc.content.size;
    const rawFrom = typeof rangeHint.from === 'number' ? rangeHint.from : view.state.selection.from;
    const rawTo = typeof rangeHint.to === 'number' ? rangeHint.to : view.state.selection.to;
    const anchorPos = Math.max(0, Math.min(Math.min(rawFrom, rawTo), docSize));
    const range = findWikidataMarkRange(view.state, anchorPos, rangeHint.uid ?? null);
    const from = Math.max(0, Math.min(range?.from ?? Math.min(rawFrom, rawTo), docSize));
    const to = Math.max(0, Math.min(range?.to ?? Math.max(rawFrom, rawTo), docSize));
    if (from >= to) {
        return {applied: false, from, to, uid: null};
    }

    const existingMarks = collectWikidataMarksInRange(view.state, from, to, markType);
    const mentionMark = existingMarks.find((mark) => !isLinkedWikidataId(mark.attrs?.id));
    const preserveUid = mentionMark?.attrs?.uid || rangeHint.uid || null;
    const user = getUser();
    const linkedBy = user?.displayName || user?.email || user?.id || null;

    const optimisticAttrs = buildWikidataEntityMarkAttrs(entity, {
        uid: preserveUid || undefined,
        nerScore: mentionMark?.attrs?.nerScore ?? mentionMark?.attrs?.confidence ?? null,
        entityType: coalesceEntityType(mentionMark?.attrs?.entityType, entity?.entityType),
        linkingScore: 1,
        source: MANUAL_WIKIDATA_LINK_SOURCE,
        linkedBy,
    });

    const mark = markType.create(optimisticAttrs);
    let tr = view.state.tr
        .removeMark(from, to, markType)
        .addMark(from, to, mark);
    const collapsePos = Math.min(to, tr.doc.content.size);
    tr = tr.setSelection(TextSelection.create(tr.doc, collapsePos, collapsePos));
    tr.removeStoredMark(markType);

    view.dispatch(tr);

    return {applied: true, from, to, uid: optimisticAttrs.uid};
}

/**
 * Add a Wikidata entity mark to the selected text
 * @param {EditorView} view - ProseMirror editor view
 * @param {Object} entity - Entity data {id, label, url}
 * @returns {boolean} True if mark was added
 */
export function addWikidataEntityMark(view, entity) {
    const {state, dispatch} = view;
    const {from, to} = state.selection;

    if (from === to) return false;

    const {schema} = state;
    const markType = schema.marks.wikidataEntity;

    if (!markType) return false;

    const mark = markType.create(buildWikidataEntityMarkAttrs(entity));

    const tr = state.tr.addMark(from, to, mark);
    dispatch(tr);

    return true;
}

/**
 * Check if the selection has a Wikidata entity mark
 * @param {EditorState} state - ProseMirror editor state
 * @returns {Object|null} Entity mark attributes or null
 */
export function getWikidataEntityAtSelection(state) {
    const {from, to} = state.selection;
    const {schema} = state;
    const markType = schema.marks.wikidataEntity;

    if (!markType) return null;

    let mark = null;
    state.doc.nodesBetween(from, to, (node) => {
        if (mark) return false;
        const found = node.marks.find(m => m.type === markType);
        if (found) {
            mark = found;
            return false;
        }
    });

    return mark ? mark.attrs : null;
}

/**
 * Remove Wikidata entity mark from selection
 * @param {EditorView} view - ProseMirror editor view
 * @returns {boolean} True if mark was removed
 */
export function removeWikidataEntityMark(view) {
    const {state, dispatch} = view;
    const {from, to} = state.selection;
    const {schema} = state;
    const markType = schema.marks.wikidataEntity;

    if (!markType) return false;

    const tr = state.tr.removeMark(from, to, markType);
    dispatch(tr);

    return true;
}

/**
 * Update wikidataEntity mark attrs for a specific mark instance uid.
 * Keeps the original range and replaces only the matched mark instance.
 * @param {EditorView} view
 * @param {string} uid
 * @param {Object} patch
 * @param {Object} [options]
 * @param {boolean} [options.addToHistory=false]
 * @returns {boolean}
 */
export function updateWikidataEntityMarkByUid(view, uid, patch, options = {}) {
    if (!view || !uid || !patch) return false;

    const {state} = view;
    const markType = state?.schema?.marks?.wikidataEntity;
    if (!markType) return false;

    const segments = [];
    state.doc.descendants((node, pos) => {
        if (!node.isText) return;
        const mark = node.marks.find((m) => m.type === markType && m.attrs?.uid === uid);
        if (!mark) return;
        segments.push({
            from: pos,
            to: pos + node.nodeSize,
            mark,
            attrs: mark.attrs || {},
        });
    });

    if (!segments.length) return false;

    const selectionBookmark = state.selection.getBookmark();

    let tr = state.tr;
    if (options.addToHistory === false) {
        tr = tr.setMeta("addToHistory", false);
    }

    for (const segment of segments) {
        const nextAttrs = {
            ...segment.attrs,
            ...patch,
            uid,
        };
        tr = tr.removeMark(segment.from, segment.to, segment.mark);
        tr = tr.addMark(segment.from, segment.to, markType.create(nextAttrs));
    }

    try {
        tr = tr.setSelection(selectionBookmark.resolve(tr.doc));
    } catch (error) {
        // Keep default mapped selection when bookmark cannot resolve.
    }

    if (!tr.docChanged) return false;
    view.dispatch(tr);
    return true;
}
